<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { pageById } from '@/data/registry'
import { usePrototypeStore } from '@/stores/prototype'
import ProTablePage from '@/components/pro/ProTablePage.vue'
import AppIcon from '@/components/AppIcon.vue'
import { formatDateTime, formatNumber, formatPhone } from '@/utils/format'
import AppDrawer from '@/components/ui/AppDrawer.vue'
import ProSelect from '@/components/pro/ProSelect.vue'

const definition = pageById('users')
if (!definition) throw new Error('用户管理页面定义缺失')

const store = usePrototypeStore()
const router = useRouter()
const route = useRoute()
const db = store.db

const users = computed(() => db.users)
const families = computed(() => db.families)
const alerts = computed(() => db.alerts)

const focusFamily = computed(() => {
  return [...families.value]
    .sort((left, right) => {
      const weight: Record<string, number> = { 高风险: 3, 中风险: 2, 低风险: 1 }
      return (weight[String(right.risk_level)] ?? 0) - (weight[String(left.risk_level)] ?? 0)
    })[0]
})

const focusMembers = computed(() => {
  if (!focusFamily.value) return []
  return users.value.filter(user => user.family_id === focusFamily.value?.id).slice(0, 3)
})

const openAlerts = computed(() => alerts.value.filter(alert => alert.status === 'pending' || alert.status === 'processing'))
const metricDigest = computed(() => {
  const groups = ['血压', '血糖', '心率']
  return groups.map((type, index) => {
    const rows = openAlerts.value.filter(alert => String(alert.alert_type).includes(type))
    return {
      type,
      count: rows.length,
      high: rows.filter(alert => alert.severity === '高').length,
      tone: index === 0 ? 'danger' : index === 1 ? 'warning' : 'info',
      icon: index === 0 ? 'heart' : index === 1 ? 'drop' : 'activity',
    }
  })
})

const privacyOpen = computed(() => route.query.tab === 'privacy')
const privacyKeyword = ref('')
const privacyScope = ref('')
const privacyOptions = [
  { value: 'health_data_sharing', label: '健康数据共享' },
  { value: 'family_sharing', label: '家庭共享' },
  { value: 'message_delivery', label: '消息触达' },
  { value: 'device_data', label: '设备数据采集' },
  { value: 'third_party', label: '第三方服务' },
]
const CONSENT_ALIASES: Record<string, string> = {
  '健康数据共享': 'health_data_sharing',
  '家庭成员查看': 'family_sharing',
  '家庭共享': 'family_sharing',
  '消息触达': 'message_delivery',
  '设备数据采集': 'device_data',
  '第三方服务': 'third_party',
}

function grantedConsents(user: Record<string, unknown>): string[] {
  const raw = Array.isArray(user.consent_scope) ? user.consent_scope.map(String) : []
  return Array.from(new Set(raw.map(value => CONSENT_ALIASES[value] ?? value).filter(value => privacyOptions.some(item => item.value === value))))
}

const privacyRows = computed(() => {
  const keyword = privacyKeyword.value.trim()
  return users.value
    .filter(user => ['active', 'pending_verification'].includes(String(user.status)))
    .map(user => ({ user, granted: grantedConsents(user) }))
    .filter(row => !keyword || `${row.user.name}${row.user.phone}${row.user.family_name}`.includes(keyword))
    .filter(row => !privacyScope.value || row.granted.includes(privacyScope.value))
})
const privacySummary = computed(() => privacyOptions.map(option => ({
  ...option,
  count: users.value.filter(user => ['active', 'pending_verification'].includes(String(user.status)) && grantedConsents(user).includes(option.value)).length,
})))

function closePrivacy(): void {
  const query = Object.fromEntries(Object.entries(route.query).filter(([key]) => key !== 'tab'))
  void router.replace({ path: route.path, query })
}

function viewPrivacyUser(user: Record<string, unknown>): void {
  closePrivacy()
  void router.push({ path: '/users', query: { keyword: String(user.name ?? '') } })
}

const recentAlerts = computed(() => [...openAlerts.value]
  .sort((left, right) => String(right.triggered_at).localeCompare(String(left.triggered_at)))
  .slice(0, 4))

function go(path: string): void {
  void router.push(path)
}

function goFamily(name: unknown): void {
  void router.push({ path: '/families', query: { focus: String(name) } })
}

function goAlert(): void {
  void router.push('/health/alerts')
}
</script>

<template>
  <ProTablePage :definition="definition">
    <template #after-table>
      <section class="user-panels" aria-label="家庭与健康概览">
        <article class="card user-panel">
          <header class="card__head">
            <h2 class="card__title">家庭详情</h2>
            <button type="button" class="card__link" @click="goFamily(focusFamily?.name)">查看完整档案 <AppIcon name="chevron-right" :size="12" /></button>
          </header>
          <div v-if="focusFamily" class="family-focus">
            <div class="family-focus__head">
              <span class="avatar avatar--lg">{{ String(focusFamily.name).slice(0, 1) }}</span>
              <div>
                <div class="family-focus__title">{{ focusFamily.name }} <span class="tag" :class="focusFamily.risk_level === '高风险' ? 'tag--danger' : focusFamily.risk_level === '中风险' ? 'tag--warning' : 'tag--success'">{{ focusFamily.risk_level }}家庭</span></div>
                <p>{{ focusFamily.owner_name }} · {{ focusFamily.member_count }} 位成员 · {{ focusFamily.region_name }}</p>
              </div>
            </div>
            <dl class="family-focus__meta">
              <div><dt>联系电话</dt><dd>{{ String(focusFamily.owner_phone || '138****6626') }}</dd></div>
              <div><dt>家庭地址</dt><dd>{{ focusFamily.address || '—' }}</dd></div>
            </dl>
            <div class="family-focus__members">
              <div class="family-focus__members-head"><span>家庭成员（{{ focusFamily.member_count }}）</span><button type="button" class="text-button" @click="go('/families')">管理成员</button></div>
              <button v-for="member in focusMembers" :key="String(member.id)" type="button" class="family-member" @click="goFamily(focusFamily.name)">
                <span class="avatar avatar--xs">{{ String(member.name).slice(-2) }}</span>
                <span class="family-member__main"><b>{{ member.name }}</b><small>{{ member.age }} 岁 · {{ member.family_role }}</small></span>
                <span class="family-member__tags">
                  <span v-for="tag in (member.tags || []).slice(0, 2)" :key="tag" class="tag tag--neutral tag--plain">{{ tag }}</span>
                </span>
              </button>
            </div>
          </div>
          <div v-else class="empty-state" style="min-height: 180px"><h3 class="empty-state__title">尚未建立家庭档案</h3><p class="empty-state__text">在家庭档案中新增家庭后，这里会显示重点家庭与成员。</p></div>
        </article>

        <article class="card user-panel">
          <header class="card__head">
            <h2 class="card__title">健康数据概览</h2>
            <button type="button" class="card__link" @click="go('/health/monitoring')">查看详情 <AppIcon name="chevron-right" :size="12" /></button>
          </header>
          <div class="metric-digest">
            <button v-for="item in metricDigest" :key="item.type" type="button" class="metric-digest__item" @click="go('/health/monitoring')">
              <span class="metric-digest__icon" :class="`metric-digest__icon--${item.tone}`"><AppIcon :name="item.icon" :size="17" /></span>
              <span class="metric-digest__label">{{ item.type }}异常</span>
              <b>{{ item.count }} 次</b>
              <small>高风险 {{ item.high }} 次</small>
            </button>
          </div>
          <div class="adherence">
            <div class="adherence__head"><span>近7日用药完成率</span><b>{{ formatNumber(users.filter(user => user.status === 'active').length) }} 位活跃用户</b></div>
            <div class="adherence__bar"><i :style="{ width: `${Math.min(100, Math.round((openAlerts.length / Math.max(1, users.length)) * 1000) / 10)}%` }" /></div>
            <p>异常预警占全部在册用户 {{ (openAlerts.length / Math.max(1, users.length) * 100).toFixed(1) }}%，高风险记录优先进入随访队列。</p>
          </div>
        </article>

        <article class="card user-panel">
          <header class="card__head">
            <h2 class="card__title">近期预警提醒</h2>
            <button type="button" class="card__link" @click="goAlert">查看全部 <AppIcon name="chevron-right" :size="12" /></button>
          </header>
          <div class="alert-mini">
            <button v-for="alert in recentAlerts" :key="String(alert.id)" type="button" class="alert-mini__row" @click="goAlert">
              <span class="alert-mini__icon" :class="alert.severity === '高' ? 'is-danger' : 'is-warning'"><AppIcon :name="String(alert.alert_type).includes('血糖') ? 'drop' : 'heart'" :size="14" /></span>
              <span class="alert-mini__main">
                <b>{{ alert.alert_type }}异常</b>
                <small>{{ alert.user_name }} · {{ alert.metric_value }}</small>
              </span>
              <span class="tag" :class="alert.severity === '高' ? 'tag--danger' : 'tag--warning'">{{ alert.severity }}风险</span>
            </button>
            <div v-if="!recentAlerts.length" class="empty-state" style="min-height: 140px"><h3 class="empty-state__title">暂无待处理预警</h3><p class="empty-state__text">新的异常上报会进入预警队列。</p></div>
          </div>
        </article>
      </section>
    </template>
  </ProTablePage>

  <AppDrawer
    :open="privacyOpen"
    title="隐私与授权"
    subtitle="只读查看用户授权范围；管理员不得代替用户授予、撤回或伪造授权"
    size="680px"
    @close="closePrivacy"
  >
    <div class="privacy-drawer">
      <div class="info-banner info-banner--brand" role="status">
        <AppIcon name="shield" :size="14" />
        <span>用户授权变更由用户在“隐私与授权”页面完成。后台仅保留查看与历史追溯入口；撤回后消息触达、家庭共享与健康数据消费端会立即停止。</span>
      </div>

      <div class="privacy-summary" aria-label="授权范围汇总">
        <div v-for="item in privacySummary" :key="item.value">
          <span>{{ item.label }}</span>
          <b>{{ item.count }}</b>
          <small>位用户已授权</small>
        </div>
      </div>

      <div class="privacy-toolbar">
        <div class="search-field">
          <AppIcon name="search" :size="15" class="search-field__icon" />
          <el-input v-model="privacyKeyword" placeholder="搜索用户、手机号或家庭" clearable aria-label="搜索授权用户" />
        </div>
        <ProSelect
          v-model="privacyScope"
          :options="privacyOptions"
          placeholder="全部授权范围"
          clearable
          aria-label="授权范围"
        />
      </div>

      <div class="privacy-table-wrap">
        <table class="pro-table privacy-table">
          <caption>用户隐私授权只读列表，共 {{ privacyRows.length }} 位用户</caption>
          <thead><tr><th>用户</th><th>已授权范围</th><th>授权状态</th><th>最近更新</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-for="row in privacyRows" :key="String(row.user.id)">
              <td>
                <span class="cell-main">
                  <span class="avatar avatar--xs">{{ String(row.user.name).slice(-2) }}</span>
                  <span class="cell-main__text"><span class="cell-main__title">{{ row.user.name }}</span><span class="cell-main__sub">{{ formatPhone(row.user.phone) }} · {{ row.user.family_name || '未加入家庭' }}</span></span>
                </span>
              </td>
              <td>
                <span v-if="row.granted.length" class="tag-row">
                  <span v-for="value in row.granted" :key="value" class="tag tag--success tag--plain">{{ privacyOptions.find(item => item.value === value)?.label }}</span>
                </span>
                <span v-else class="cell-muted">未授权</span>
              </td>
              <td><span class="tag" :class="row.granted.length >= 4 ? 'tag--success' : row.granted.length ? 'tag--warning' : 'tag--neutral'">{{ row.granted.length >= 4 ? '主要范围已授权' : row.granted.length ? '部分授权' : '未授权' }}</span></td>
              <td class="is-datetime">{{ formatDateTime(row.user.updated_at) }}</td>
              <td class="is-actions"><button type="button" class="row-action" @click="viewPrivacyUser(row.user)">查看用户</button></td>
            </tr>
            <tr v-if="!privacyRows.length"><td colspan="5" class="cell-muted">没有符合当前筛选条件的授权记录</td></tr>
          </tbody>
        </table>
      </div>
      <p class="privacy-note">授权范围与策略版本由用户端提交并保留历史；当前原型只展示当前内存快照，不展示正式凭据或第三方共享明细。</p>
    </div>
  </AppDrawer>
</template>

<style scoped>
.user-panels { display: grid; grid-template-columns: minmax(0, 1.12fr) minmax(0, 1fr) minmax(0, 1.06fr); gap: 14px; margin-top: 14px; }
.user-panel { min-width: 0; overflow: hidden; }
.family-focus { padding: 2px 16px 16px; }
.family-focus__head { display: flex; align-items: center; gap: 10px; }
.avatar--lg { width: 42px; height: 42px; font-size: 14px; }
.family-focus__title { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: var(--c-ink); }
.family-focus__head p { margin-top: 3px; font-size: 10px; color: var(--c-muted); }
.family-focus__meta { display: grid; gap: 5px; margin: 11px 0; padding: 9px 10px; border-radius: 8px; background: var(--c-surface-muted); }
.family-focus__meta div { display: flex; gap: 8px; font-size: 10px; }
.family-focus__meta dt { flex: 0 0 54px; color: var(--c-faint); }
.family-focus__meta dd { margin: 0; color: var(--c-text); }
.family-focus__members { border-top: 1px solid var(--c-divider); padding-top: 9px; }
.family-focus__members-head { display: flex; align-items: center; justify-content: space-between; font-size: 10px; color: var(--c-muted); margin-bottom: 4px; }
.family-member { width: 100%; display: flex; align-items: center; gap: 7px; padding: 6px 0; border: 0; background: transparent; text-align: left; border-top: 1px dashed var(--c-divider); }
.family-member:first-of-type { border-top: 0; }
.family-member__main { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.family-member__main b { font-size: 11px; color: var(--c-text); }
.family-member__main small { font-size: 9px; color: var(--c-faint); }
.family-member__tags { display: flex; gap: 3px; }
.metric-digest { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; padding: 2px 16px 10px; }
.metric-digest__item { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; padding: 8px; border: 1px solid var(--c-border); border-radius: 8px; background: #fff; text-align: left; }
.metric-digest__item:hover { border-color: var(--c-success-border); background: var(--c-brand-mist); }
.metric-digest__icon { width: 25px; height: 25px; display: grid; place-items: center; border-radius: 7px; }
.metric-digest__icon--danger { background: var(--c-danger-soft); color: var(--c-danger); }
.metric-digest__icon--warning { background: var(--c-warning-soft); color: #ae7008; }
.metric-digest__icon--info { background: var(--c-info-soft); color: var(--c-info); }
.metric-digest__label { font-size: 9.5px; color: var(--c-muted); }
.metric-digest__item b { font-family: var(--font-num); font-size: 17px; color: var(--c-ink); line-height: 1.1; }
.metric-digest__item small { font-size: 8.5px; color: var(--c-faint); }
.adherence { margin: 0 16px 15px; padding: 9px 10px; border-radius: 8px; background: var(--c-brand-mist); border: 1px solid #d9f0e6; }
.adherence__head { display: flex; justify-content: space-between; gap: 8px; font-size: 10px; color: #3c7a66; }
.adherence__head b { color: #0b6b50; font-weight: 600; }
.adherence__bar { height: 6px; margin: 7px 0 5px; border-radius: 4px; background: #d9eee5; overflow: hidden; }
.adherence__bar i { display: block; height: 100%; border-radius: 4px; background: var(--c-brand); }
.adherence p { font-size: 9px; line-height: 1.5; color: #4d8a77; }
.alert-mini { padding: 0 16px 12px; }
.alert-mini__row { width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 0; border: 0; border-top: 1px solid var(--c-divider); background: transparent; text-align: left; }
.alert-mini__row:first-child { border-top: 0; }
.alert-mini__icon { width: 27px; height: 27px; display: grid; place-items: center; border-radius: 8px; }
.alert-mini__icon.is-danger { background: var(--c-danger-soft); color: var(--c-danger); }
.alert-mini__icon.is-warning { background: var(--c-warning-soft); color: #b47710; }
.alert-mini__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.alert-mini__main b { font-size: 10.5px; color: var(--c-text); }
.alert-mini__main small { margin-top: 2px; font-size: 9px; color: var(--c-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
@media (max-width: 1399px) { .user-panels { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 1023px) { .user-panels { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 767px) { .metric-digest { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
</style>


<style scoped>
.privacy-drawer { display: grid; gap: 12px; padding: 16px 22px 24px; }
.privacy-summary { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 6px; }
.privacy-summary > div { padding: 9px 8px; border: 1px solid var(--c-border); border-radius: 9px; background: var(--c-surface-muted); }
.privacy-summary span { display: block; font-size: 9.5px; color: var(--c-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.privacy-summary b { display: block; margin-top: 3px; font-family: var(--font-num); font-size: 18px; color: var(--c-ink); line-height: 1; }
.privacy-summary small { display: block; margin-top: 3px; font-size: 8.5px; color: var(--c-faint); white-space: nowrap; }
.privacy-toolbar { display: flex; align-items: center; gap: 8px; }
.privacy-toolbar .search-field { flex: 1; }
.privacy-toolbar :deep(.pro-select) { width: 170px; flex: 0 0 auto; }
.privacy-table-wrap { overflow-x: auto; border: 1px solid var(--c-border); border-radius: 10px; }
.privacy-table { min-width: 610px; }
.privacy-note { margin: 0; padding: 9px 11px; border-radius: 8px; background: var(--c-surface-muted); color: var(--c-muted); font-size: 10px; line-height: 1.6; }
@media (max-width: 767px) { .privacy-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } .privacy-toolbar { flex-direction: column; align-items: stretch; } .privacy-toolbar :deep(.pro-select) { width: 100%; } }
</style>
